import { DicomMetadataStore } from '@ohif/core';
import { useImportedStudyStore } from '../../stores/useImportedStudyStore';
import { collectStudyDicomInstances } from './resolveViewportStudy';
import type { StudyDicomInstance } from './types';

const INSTANCE_METADATA_KEYS = ['StudyDate', 'StudyID', 'AccessionNumber'] as const;

export function sanitizeFilenamePart(value: string): string {
  const sanitized = value.replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '');
  return sanitized || 'UNKNOWN';
}

function firstNonEmpty(...values: unknown[]): string {
  for (const value of values) {
    const normalized = String(value ?? '').trim();
    if (normalized) {
      return normalized;
    }
  }

  return '';
}

function normalizeStudyDate(value: string): string {
  const digits = value.replace(/\D/g, '');

  if (digits.length >= 8) {
    return digits.slice(0, 8);
  }

  return digits;
}

function formatTodayAsDicomDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  return `${year}${month}${day}`;
}

function shortStudyIdentifier(studyInstanceUID: string): string {
  const segments = studyInstanceUID.split('.').filter(Boolean);
  const tail = segments[segments.length - 1] || studyInstanceUID;

  return tail.slice(-12) || 'Study';
}

function findInstanceMetadataValue(
  instances: StudyDicomInstance[],
  key: (typeof INSTANCE_METADATA_KEYS)[number]
): string {
  for (const instance of instances) {
    const value = firstNonEmpty(instance[key]);
    if (value) {
      return value;
    }
  }

  return '';
}

function collectMetadataFromDisplaySets(
  studyInstanceUID: string,
  servicesManager?: AppTypes.ServicesManager
): Partial<Record<(typeof INSTANCE_METADATA_KEYS)[number], string>> {
  if (!servicesManager) {
    return {};
  }

  const { displaySetService } = servicesManager.services;
  const displaySets = displaySetService.getDisplaySetsBy(
    displaySet => displaySet.StudyInstanceUID === studyInstanceUID
  );

  for (const displaySet of displaySets) {
    const instance = (displaySet.instances?.[0] ?? displaySet.instance) as
      | StudyDicomInstance
      | undefined;

    if (!instance) {
      continue;
    }

    const metadata = {
      StudyDate: firstNonEmpty(instance.StudyDate),
      StudyID: firstNonEmpty(instance.StudyID),
      AccessionNumber: firstNonEmpty(instance.AccessionNumber),
    };

    if (metadata.StudyDate || metadata.StudyID || metadata.AccessionNumber) {
      return metadata;
    }
  }

  return {};
}

export function getStudyFilenameParts(
  studyInstanceUID: string,
  instances: StudyDicomInstance[] = [],
  servicesManager?: AppTypes.ServicesManager
): { studyDate: string; studyId: string } {
  const resolvedInstances =
    instances.length > 0 ? instances : collectStudyDicomInstances(studyInstanceUID);
  const study = DicomMetadataStore.getStudy(studyInstanceUID);
  const importedStudy = useImportedStudyStore
    .getState()
    .studies.find(record => record.studyInstanceUID === studyInstanceUID);
  const displaySetMetadata = collectMetadataFromDisplaySets(studyInstanceUID, servicesManager);

  const rawStudyDate = firstNonEmpty(
    findInstanceMetadataValue(resolvedInstances, 'StudyDate'),
    study?.StudyDate,
    displaySetMetadata.StudyDate,
    importedStudy?.studyDate,
    formatTodayAsDicomDate()
  );

  const rawStudyId = firstNonEmpty(
    findInstanceMetadataValue(resolvedInstances, 'StudyID'),
    study?.StudyID,
    displaySetMetadata.StudyID,
    findInstanceMetadataValue(resolvedInstances, 'AccessionNumber'),
    study?.AccessionNumber,
    displaySetMetadata.AccessionNumber,
    shortStudyIdentifier(studyInstanceUID)
  );

  return {
    studyDate: sanitizeFilenamePart(normalizeStudyDate(rawStudyDate) || formatTodayAsDicomDate()),
    studyId: sanitizeFilenamePart(rawStudyId),
  };
}

export function buildStudyArchiveFilename(studyDate: string, studyId: string): string {
  return `${studyDate}_${studyId}.zip`;
}

export function buildStudyJpegFilename(
  studyDate: string,
  studyId: string,
  imageIndex: number
): string {
  const paddedIndex = String(imageIndex).padStart(3, '0');
  return `${studyDate}_${studyId}_Image${paddedIndex}.jpg`;
}
